import { useDraggable } from '@dnd-kit/core';
import { User, GripVertical, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import type { ScheduleEmployee } from '../../hooks/useScheduleBuilder';

// BOH (Kitchen) = warm amber tones, FOH (Floor) = cool slate tones
const BOH_ROLES = new Set(['cook', 'dishwasher']);

const getRoleColors = (role: string) => {
  if (BOH_ROLES.has(role)) {
    return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' };
  }
  return { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
};

interface EmployeeCardProps {
  employee: ScheduleEmployee;
  isAvailable?: boolean;
  isAssigned?: boolean;
  compact?: boolean;
}

export default function EmployeeCard({
  employee,
  isAvailable = true,
  isAssigned = false,
  compact = false,
}: EmployeeCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: employee.id,
    data: {
      type: 'employee',
      employee,
      role: employee.primaryRole,
    },
    // Don't disable drag - employees can work multiple shifts
    disabled: false,
  });

  const roleColors = getRoleColors(employee.primaryRole);

  const style: React.CSSProperties = transform
    ? {
        transform: `translate(${transform.x}px, ${transform.y}px)`,
        zIndex: isDragging ? 1000 : undefined,
      }
    : {};

  if (compact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={clsx(
          'flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border transition-all',
          roleColors.bg,
          roleColors.text,
          roleColors.border,
          isDragging && 'shadow-lg opacity-90',
          isAssigned && 'ring-1 ring-indigo-300', // Visual indicator that they have shifts
          'cursor-grab active:cursor-grabbing hover:shadow-sm'
        )}
      >
        <span className="truncate">
          {employee.firstName} {employee.lastName.charAt(0)}.
        </span>
      </div>
    );
  }

  // Check for compliance warnings
  const hasComplianceIssues = employee.hasComplianceIssues ||
    (employee.complianceWarnings && employee.complianceWarnings.length > 0) ||
    (employee.complianceBlockingReasons && employee.complianceBlockingReasons.length > 0);
  const complianceWarningsList = [
    ...(employee.complianceBlockingReasons || []),
    ...(employee.complianceWarnings || []),
  ];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={clsx(
        'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all',
        roleColors.bg,
        roleColors.border,
        isDragging && 'shadow-lg opacity-90 scale-105',
        isAssigned && 'ring-1 ring-indigo-300', // Visual indicator that they have shifts
        !isAvailable && 'opacity-40',
        isAvailable && 'cursor-grab active:cursor-grabbing hover:shadow-md',
        hasComplianceIssues && 'ring-1 ring-amber-300'
      )}
      title={hasComplianceIssues ? complianceWarningsList.join('\n') : undefined}
    >
      <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />

      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0">
        <User className={clsx('w-4 h-4', roleColors.text)} />
      </div>

      <div className="flex-1 min-w-0">
        <div className={clsx('text-sm font-medium truncate flex items-center gap-1', roleColors.text)}>
          {employee.firstName} {employee.lastName}
          {hasComplianceIssues && (
            <ShieldAlert className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          )}
        </div>
        {!isAvailable && (
          <div className="text-xs text-slate-500">Unavailable</div>
        )}
        {hasComplianceIssues && isAvailable && (
          <div className="text-[10px] text-amber-600 truncate">
            ⚠ {complianceWarningsList[0]}
          </div>
        )}
      </div>

      {/* Availability indicator */}
      <div
        className={clsx(
          'w-2 h-2 rounded-full flex-shrink-0',
          isAvailable ? (hasComplianceIssues ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-slate-300'
        )}
        title={isAvailable ? (hasComplianceIssues ? 'Available (with warnings)' : 'Available') : 'Not available'}
      />
    </div>
  );
}

// Export a simpler version for showing assigned employees in shift slots
export function AssignedEmployeeBadge({
  employee,
  onRemove,
  isHighlighted,
}: {
  employee: { firstName: string; lastName: string; role?: string };
  onRemove?: () => void;
  isHighlighted?: boolean;
}) {
  const role = employee.role || 'server';
  const roleColors = getRoleColors(role);

  return (
    <div
      className={clsx(
        'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border',
        isHighlighted
          ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
          : [roleColors.bg, roleColors.text, roleColors.border],
        onRemove && 'pr-1'
      )}
    >
      <span>{employee.firstName}</span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 rounded hover:bg-white/50 transition-colors"
          aria-label="Remove assignment"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
